import { z } from "zod";

/**
 * Skip logic: the shape of a display condition, and the one evaluator for it.
 *
 * This module sits BELOW both `surveyImport.ts` (which authors conditions) and
 * `surveys.ts` (which puts them on the wire), and it imports neither. That is
 * not tidiness: those two already reference each other, so defining the
 * conditions in either one makes the cycle a real one, and in a package that
 * initialises its schemas at import time a cycle surfaces as
 * "Cannot access X before initialization" from a file that has nothing to do
 * with surveys.
 *
 * Whether a question is shown, given what has been answered so far.
 *
 * **This is the rule written ONCE, for three readers.** The patient app decides
 * which question to navigate to next, the API decides which answers a
 * submission must carry, and (later) an export decides which blanks are real.
 * Two implementations of it would disagree in exactly the case nobody tests: a
 * patient answers what the app showed them and the server refuses the
 * submission, at the end of a 51-item questionnaire, with the draft already
 * emptied. So the app and the API import the same function from here, and
 * `surveyImport.ts` next door is where the conditions it reads are defined and
 * validated.
 *
 * The evaluator takes CODES rather than questions or options, because the two
 * callers hold different shapes: the API has Prisma rows, the app has the
 * response schema, and a research export will have neither. Each maps its own
 * rows into `VisibilityQuestion` and the rule stays out of all three.
 *
 * ## Two semantics that are decisions, not details
 *
 * **An unanswered reference is FALSE, for every operator.** A condition asks
 * about an answer, and there is not one, so the question stays hidden. Uniform
 * across `eq` / `neq` / `in` / `gte` / `lte` deliberately: reading `neq` as
 * "true when nothing was answered" is defensible in isolation and produces a
 * questionnaire whose second half appears before its first is filled in.
 *
 * **A hidden question answers nothing.** `resolveVisibility` walks in order and
 * only feeds a question's codes forward once it has decided that question was
 * shown, so a stale answer left in a draft cannot reach through and reveal
 * something downstream. That is what makes chains (KPREM's Q04 -> Q05 -> Q06)
 * collapse correctly when the top of the chain changes, and it is why this is a
 * sequential pass rather than a per-question predicate anybody can call in any
 * order. The import format guarantees the pass is enough: `visibleIf` may only
 * reference an EARLIER question, so one forward walk resolves every condition
 * with no fixpoint and no possibility of a cycle.
 */

/**
 * Question keys / domain keys: word chars plus `.` and `-`, e.g. "PPE-15_Q10.1".
 *
 * Here rather than in `surveyImport.ts` because a condition names a question by
 * this key, and one definition of what a key looks like is the point.
 */
export const SurveyKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, {
    message:
      "Keys must start with a letter or digit and contain only letters, digits, '.', '_' or '-'",
  });

/**
 * A single comparison against an earlier question's answer.
 * - `eq` / `neq` / `in` compare against a SINGLE question's option values or a
 *   RANGE question's numeric answer.
 * - `gte` / `lte` compare against a RANGE question's numeric answer only.
 * Referencing MULTIPLE questions is not supported in version 1.
 */
const ConditionEqSchema = z.strictObject({
  question: SurveyKeySchema,
  op: z.literal("eq"),
  value: z.number().int(),
});
const ConditionNeqSchema = z.strictObject({
  question: SurveyKeySchema,
  op: z.literal("neq"),
  value: z.number().int(),
});
const ConditionInSchema = z.strictObject({
  question: SurveyKeySchema,
  op: z.literal("in"),
  values: z.array(z.number().int()).min(1),
});
const ConditionGteSchema = z.strictObject({
  question: SurveyKeySchema,
  op: z.literal("gte"),
  value: z.number().int(),
});
const ConditionLteSchema = z.strictObject({
  question: SurveyKeySchema,
  op: z.literal("lte"),
  value: z.number().int(),
});

export const SurveyImportConditionSchema = z.discriminatedUnion("op", [
  ConditionEqSchema,
  ConditionNeqSchema,
  ConditionInSchema,
  ConditionGteSchema,
  ConditionLteSchema,
]);
export type SurveyImportCondition = z.infer<typeof SurveyImportConditionSchema>;

/**
 * `visibleIf` is either a single condition, or a one-level `all` (AND) /
 * `any` (OR) combinator. A question with no `visibleIf` is always shown.
 */
export const SurveyImportVisibleIfSchema = z.union([
  SurveyImportConditionSchema,
  z.strictObject({ all: z.array(SurveyImportConditionSchema).min(1) }),
  z.strictObject({ any: z.array(SurveyImportConditionSchema).min(1) }),
]);
export type SurveyImportVisibleIf = z.infer<typeof SurveyImportVisibleIfSchema>;

/** One question's contribution: who it is, when it shows, what it was answered with. */
export type VisibilityQuestion = {
  /**
   * The instrument's variable id. Null on a question authored before the
   * importer existed, which no condition can reference and which is therefore
   * always visible.
   */
  key: string | null;
  /** Null or undefined means "always shown". */
  visibleIf?: SurveyImportVisibleIf | null;
  /**
   * The response codes this question currently holds: the selected options'
   * values for a choice question, the magnitude for a RANGE one, and an empty
   * array (or undefined) for one that has not been answered.
   */
  codes?: readonly number[] | undefined;
};

function satisfies(
  condition: SurveyImportCondition,
  codes: readonly number[] | undefined,
): boolean {
  // No answer, no satisfaction — whatever the operator. See the module note.
  if (!codes || codes.length === 0) return false;

  switch (condition.op) {
    case "eq":
      return codes.includes(condition.value);
    // "None of the codes is this one", rather than "some code is not this one":
    // the two differ only on a MULTIPLE question, which the import schema
    // refuses to let a condition reference, and this is the reading that stays
    // correct if it ever stops refusing.
    case "neq":
      return !codes.includes(condition.value);
    case "in":
      return codes.some((code) => condition.values.includes(code));
    case "gte":
      return codes.some((code) => code >= condition.value);
    case "lte":
      return codes.some((code) => code <= condition.value);
  }
}

/**
 * Evaluate one `visibleIf` against a lookup of what has been answered.
 *
 * Exported for the callers that hold a single question and already know the
 * answers before it (a preview, a test). Prefer `resolveVisibility` for a whole
 * questionnaire: it is the one that enforces "a hidden question answers
 * nothing", which this function cannot know about on its own.
 */
export function isVisibleWith(
  visibleIf: SurveyImportVisibleIf | null | undefined,
  codesFor: (questionKey: string) => readonly number[] | undefined,
): boolean {
  if (!visibleIf) return true;

  if ("all" in visibleIf)
    return visibleIf.all.every((c) => satisfies(c, codesFor(c.question)));
  if ("any" in visibleIf)
    return visibleIf.any.some((c) => satisfies(c, codesFor(c.question)));
  return satisfies(visibleIf, codesFor(visibleIf.question));
}

/**
 * Which of a questionnaire's questions are shown, in the same order.
 *
 * `questions` must be in the questionnaire's own order (`position`), because
 * that is the order `visibleIf`'s forward-only rule is stated against. Out of
 * order, a condition would read a question the patient has not reached.
 */
export function resolveVisibility(
  questions: readonly VisibilityQuestion[],
): boolean[] {
  const answered = new Map<string, readonly number[]>();

  return questions.map((question) => {
    const visible = isVisibleWith(question.visibleIf, (key) =>
      answered.get(key),
    );
    // Only a SHOWN question's answer travels forward. A hidden one may still
    // carry a stale answer in a draft, and letting that through is how a chain
    // half-collapses.
    if (visible && question.key && question.codes?.length)
      answered.set(question.key, question.codes);
    return visible;
  });
}

/** The subset that is shown, for callers that want the questions rather than the flags. */
export function visibleOnly<T extends VisibilityQuestion>(
  questions: readonly T[],
): T[] {
  const shown = resolveVisibility(questions);
  return questions.filter((_, i) => shown[i]);
}
